/**
 * CANTO Innovation Challenge — content source of truth.
 *
 * The public page renders itself from this file. To move the competition
 * forward each year, the only things that usually change are:
 *
 *   1. `stage`  — controls which "Dynamic Competition" section shows.
 *   2. `teams`  — the Top 10 / Top 5 roster (see notes on that field).
 *   3. `finalists` — the Top 3 once final results are in.
 *   4. `hallOfWinners` — append the new champions after each cycle.
 *
 * No component or CSS edits are needed to advance a cycle.
 */

const innovationChallenge = {
  // Current competition year, shown in the hero + dynamic section.
  year: 2026,

  /**
   * Drives the whole page. One of:
   *   'applications' — sign-ups open/closed, shows deadline + eligibility + prizes
   *   'top10'        — reveals the Top 10 information cards
   *   'top5'         — same cards, narrowed to the five that advanced
   *   'winners'      — replaces cards with the Top 3 finalists (with photos)
   */
  stage: 'applications',

  contactEmail: 'jdiaz@canto.org',

  hero: {
    title: 'CANTO Innovation Challenge',
    intro:
      'A regional platform where young Caribbean innovators build tech-driven ' +
      'solutions to real problems — with mentorship, exposure, and a stage at ' +
      'CANTO Connect.',
  },

  // ── Applications stage ──────────────────────────────────────────────
  applications: {
    open: false, // flip to true while sign-ups are live
    deadline: 'May 25, 2026',
    applyUrl: 'https://forms.gle/xSkENtD84R59fvXr6',
    eligibility: [
      'Teams of Caribbean-based innovators',
      'Students, early-career professionals & entrepreneurs',
      'A solution mapped to one Innovation Track',
    ],
    prizes: [
      'Cash prizes for the top teams',
      'One-on-one mentorship from industry leaders',
      'Recognition on the CANTO Connect stage',
    ],
  },

  // ── About: icon cards (kept short on purpose) ───────────────────────
  about: [
    {
      icon: 'lightbulb',
      title: 'What it is',
      body: 'A regional competition to design creative, tech-driven solutions for the Caribbean ICT sector.',
    },
    {
      icon: 'users',
      title: 'Who can join',
      body: 'Young innovators across the region — students, entrepreneurs, and early-career technologists.',
    },
    {
      icon: 'rocket',
      title: 'Why participate',
      body: 'Turn an idea into a real product with guidance, and pitch it to the regional ICT community.',
    },
    {
      icon: 'award',
      title: 'Key benefits',
      body: 'Mentorship, cash prizes, exposure, and a place in the CANTO innovation network.',
    },
  ],

  // ── Innovation Tracks ───────────────────────────────────────────────
  tracks: [
    {
      icon: 'climate',
      title: 'Climate & Disaster Resilience',
      body: 'Tools that help communities prepare for, withstand, and recover from climate shocks.',
    },
    {
      icon: 'government',
      title: 'Digital Government & Public Services',
      body: 'Solutions that make public services faster, clearer, and easier to reach.',
    },
    {
      icon: 'health',
      title: 'Health, Well-Being & Inclusive Technology',
      body: 'Technology that improves health outcomes and reaches people often left out.',
    },
    {
      icon: 'education',
      title: 'Education, Skills & Workforce Development',
      body: 'Platforms that build skills and open pathways into the digital economy.',
    },
    {
      icon: 'business',
      title: 'Smart Businesses, Tourism & Economic Growth',
      body: 'Ideas that grow local businesses, tourism, and regional economic activity.',
    },
  ],

  /**
   * Top 10 / Top 5 roster (no photos by design — clean info cards).
   * Show all of these at the 'top10' stage. Mark the five that advance
   * with `top5: true`; the 'top5' stage automatically filters to those.
   */
  teams: [
    // Example shape — replace with the real roster when Top 10 is announced:
    // { team: 'Team 7.5', project: "Where's My Bus?", country: 'Jamaica', track: 'Smart Businesses, Tourism & Economic Growth', top5: true },
  ],

  /**
   * Top 3 finalists — shown only at the 'winners' stage. Photos live in
   * /public/assets/. `placement` is 1, 2, or 3.
   */
  finalists: [
    // { placement: 1, team: '', project: '', country: '', track: '', photo: '/assets/...' },
  ],

  // ── Hall of Winners: permanent archive, newest first ────────────────
  hallOfWinners: [
    {
      year: 2025,
      team: 'Team 7.5',
      country: 'Jamaica',
      project: "Where's My Bus?",
      summary:
        'A mobile solution giving commuters real-time bus locations, arrival ' +
        'times, and capacity — cutting wait times and supporting smarter ' +
        'urban transit planning.',
      photo: '/assets/Innovation_Challenge_1st.png',
    },
  ],

  // ── Gallery: recognition moments at CANTO Connect ───────────────────
  gallery: [
    { src: '/assets/Innovation_Challenge_winners.jpg', caption: '2025 winners at CANTO Connect' },
  ],

  // ── Sponsors & Partners ─────────────────────────────────────────────
  sponsors: [
    // { name: '', logo: '/assets/sponsors/....png', url: '' },
  ],

  // ── Mentorship callout (kept from the previous page) ────────────────
  mentorship: {
    title: 'Become a Mentor',
    lead: 'Passionate about the next generation of Caribbean innovators?',
    body:
      'Guide young teams as they shape the region’s digital future. To get ' +
      'involved, contact Jenelle Edwards-Diaz.',
    guideUrl: '/assets/CANTO_Innovation_Mentorship_Programme.pdf',
  },
};

export default innovationChallenge;
